// C10 — Invitation lookup + accept/decline.
//
// Pairs with the C6 backend endpoints. Same error-unwrap conventions
// as collabApi: throws Error with `.code` so the page can branch by
// machine-readable code (`invite_expired`, `email_mismatch`, ...).

import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';
const AUTH_TOKEN_KEY = 'ivisora_auth_token';
const DESIGNER_TOKEN_KEY = 'user_token';

const getToken = () =>
  localStorage.getItem(AUTH_TOKEN_KEY) || localStorage.getItem(DESIGNER_TOKEN_KEY);

const authHeaders = () => {
  const t = getToken();
  return t
    ? { 'Content-Type': 'application/json', Authorization: `Bearer ${t}` }
    : { 'Content-Type': 'application/json' };
};

function unwrapError(err) {
  const status = err?.response?.status;
  const detail = err?.response?.data?.detail;
  if (detail && typeof detail === 'object') {
    const e = new Error(detail.message || 'Request failed');
    e.code = detail.code;
    e.status = status;
    e.detail = detail;
    return e;
  }
  // Map framework-level errors (401 from get_current_user, etc.) to
  // friendly copy. See collabApi.js for the shared pattern.
  const friendly = friendlyByStatus(status);
  const message = friendly?.message
    || (typeof detail === 'string' ? detail : err?.message)
    || 'Request failed';
  const e = new Error(message);
  e.code = friendly?.code || null;
  e.status = status;
  return e;
}

function friendlyByStatus(status) {
  switch (status) {
    case 401: return { code: 'session_expired',
                       message: 'Your session has expired. Please sign in again.' };
    case 403: return { code: 'forbidden',
                       message: "You don't have permission to do that." };
    default: return null;
  }
}

async function call(method, url, body) {
  try {
    const res = await axios.request({
      method, url, data: body, headers: authHeaders(),
    });
    return res.data;
  } catch (err) {
    throw unwrapError(err);
  }
}

export const invitationApi = {
  // Public lookup. No auth header sent — backend doesn't need it.
  lookup: (token) =>
    call('get', `${API_URL}/api/invitations/${encodeURIComponent(token)}`),

  // Both require auth + strict email match (C6 enforces).
  accept: (token) =>
    call('post', `${API_URL}/api/invitations/${encodeURIComponent(token)}/accept`),

  decline: (token) =>
    call('post', `${API_URL}/api/invitations/${encodeURIComponent(token)}/decline`),
};

// State-to-copy mapping. Pages import this so terminal-state copy
// stays in sync across surfaces.
export const INVITATION_TERMINAL_COPY = {
  invite_expired: {
    title: 'This invitation has expired',
    body: 'Invitations are valid for 7 days. Ask the owner to send a new one.',
  },
  invite_declined: {
    title: 'You declined this invitation',
    body: 'If this was a mistake, the owner can send a new one.',
  },
  invite_removed: {
    title: 'This invitation was revoked',
    body: 'The owner cancelled this invitation. Get in touch if you think this is a mistake.',
  },
  invite_not_found: {
    title: "We can't find this invitation",
    body: "The link may have a typo, or the invitation may have been deleted.",
  },
  email_mismatch: {
    title: 'Sign in as the invited email',
    body: "This invitation was sent to a different account.",
  },
};
